import React, { useState, useEffect } from 'react';
import { Users, Settings, Shield, UserPlus, Save, AlertCircle, Loader2, RefreshCw, Building2, Plus, X } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAdminFeatures } from '../hooks/useUserRole';
import { RolePermissionManager } from './RolePermissionManager';
import { DatabaseService } from '../lib/database';
import type { UserRole, UserCreateRequest, Permission, UserDeal, DealAccessType } from '../types/database';

export function AdminPanel() {
  const { user } = useUser();
  const { 
    isAdmin,
    isSuperAdmin,
    isLoading: isLoadingPermissions,
    canManageUsers, 
    allUsers, 
    refreshUsers, 
    updateUserRole, 
    createUser, 
    deactivateUser,
    reactivateUser,
    toggleSuperAdmin,
    deleteUser,
    impersonateUser,
    updateRolePermissions
  } = useAdminFeatures();
  
  const [newUser, setNewUser] = useState<UserCreateRequest>({ 
    email: '', 
    first_name: '', 
    last_name: '', 
    role: 'Lender' 
  });
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  
  // Deal management state
  const [availableDeals, setAvailableDeals] = useState<UserDeal[]>([]);
  const [selectedDeals, setSelectedDeals] = useState<{dealId: string, accessType: DealAccessType}[]>([]);
  const [isLoadingDeals, setIsLoadingDeals] = useState(false);

  // Load available deals on component mount
  useEffect(() => {
    const loadDeals = async () => {
      if (!user?.id) return;
      
      setIsLoadingDeals(true);
      try {
        // Admin users get 'write' access to see all deals
        const response = await DatabaseService.getUserDeals('write', user.id);
        if (response.data) {
          setAvailableDeals(response.data);
        }
      } catch (error) {
        console.error('Error loading deals:', error);
      } finally {
        setIsLoadingDeals(false);
      }
    };

    if (canManageUsers && user?.id) {
      loadDeals();
    }
  }, [canManageUsers, user?.id]);

  const handleUpdateUserRole = async (userId: string, newRole: UserRole) => {
    setIsLoading(true);
    try {
      const response = await updateUserRole({ user_id: userId, new_role: newRole });
      if (response.error) {
        setMessage(`Error: ${response.error}`);
      } else {
        setMessage(`Updated user role to ${newRole}`);
      }
    } catch (error) {
      setMessage(`Error updating user role: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.role) {
      setMessage('Please fill in all required fields');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsLoading(true);
    try {
      const response = await createUser(newUser);
      if (response.error) {
        setMessage(`Error: ${response.error}`);
      } else {
        // Grant deal access if deals are selected
        if (selectedDeals.length > 0 && response.data) {
          for (const dealAccess of selectedDeals) {
            try {
              await DatabaseService.grantDealAccess({
                deal_id: dealAccess.dealId,
                user_id: response.data.user_id,
                access_type: dealAccess.accessType
              });
            } catch (dealError) {
              console.error('Error granting deal access:', dealError);
              setMessage(`User created but failed to grant access to some deals`);
              break;
            }
          }
        }
        
        setMessage('User added successfully');
        setNewUser({ email: '', first_name: '', last_name: '', role: 'Lender' });
        setSelectedDeals([]);
      }
    } catch (error) {
      setMessage(`Error creating user: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleAddDealAccess = () => {
    setSelectedDeals([...selectedDeals, { dealId: '', accessType: 'read' }]);
  };

  const handleRemoveDealAccess = (index: number) => {
    setSelectedDeals(selectedDeals.filter((_, i) => i !== index));
  };

  const handleUpdateDealAccess = (index: number, field: 'dealId' | 'accessType', value: string) => {
    const updated = [...selectedDeals];
    updated[index] = { ...updated[index], [field]: value };
    setSelectedDeals(updated);
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    const action = currentStatus ? 'deactivate' : 'reactivate';
    if (!confirm(`Are you sure you want to ${action} this user? ${currentStatus ? 'Their deals will be hidden from other users.' : 'Their deals will be visible again.'}`)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = currentStatus ? await deactivateUser(userId) : await reactivateUser(userId);
      if (response.error) {
        setMessage(`Error: ${response.error}`);
      } else {
        setMessage(response.message || `User ${action}d successfully`);
      }
    } catch (error) {
      setMessage(`Error ${action}ing user: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleToggleSuperAdmin = async (userId: string, currentStatus: boolean) => {
    const action = currentStatus ? 'revoke Super Admin status from' : 'grant Super Admin status to';
    if (!confirm(`Are you sure you want to ${action} this user? ${!currentStatus ? 'Only ONE Super Admin can exist at a time.' : ''}`)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await toggleSuperAdmin(userId);
      if (response.error) {
        setMessage(`Error: ${response.error}`);
      } else {
        setMessage(response.message || `Super Admin status ${currentStatus ? 'revoked' : 'granted'}`);
      }
    } catch (error) {
      setMessage(`Error toggling Super Admin: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handlePermanentlyDeleteUser = async (userId: string) => {
    if (!confirm('⚠️ WARNING: This will PERMANENTLY DELETE the user, their Clerk account, and ALL their deals. This action CANNOT be undone. Are you absolutely sure?')) {
      return;
    }

    // Double confirmation for such a destructive action
    if (!confirm('Final confirmation: Type YES in your mind to proceed with PERMANENT DELETION')) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await deleteUser(userId);
      if (response.error) {
        setMessage(`Error: ${response.error}`);
      } else {
        setMessage(response.message || 'User permanently deleted');
      }
    } catch (error) {
      setMessage(`Error deleting user: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleImpersonateUser = async (userId: string, userEmail: string) => {
    if (!confirm(`⚠️ IMPORTANT: Impersonation requires a clean browser session.\n\nTo impersonate ${userEmail}:\n1. The URL will be copied to your clipboard\n2. Open an INCOGNITO/PRIVATE window\n3. Sign out if you're already logged in\n4. Paste and open the URL\n\nClick OK to generate the impersonation URL.`)) {
      return;
    }

    setIsLoading(true);
    setMessage('Generating impersonation token...');
    
    try {
      const response = await impersonateUser(userId);
      if (response.error) {
        setMessage(`Error: ${response.error}`);
        setTimeout(() => setMessage(''), 5000);
      } else if (response.data) {
        const { url, email, name } = response.data;
        
        if (url) {
          const appUrl = window.location.origin;
          const redirectUrl = `${url}&redirect_url=${encodeURIComponent(appUrl)}`;
          
          // Copy to clipboard
          navigator.clipboard.writeText(redirectUrl).then(() => {
            setMessage(`✅ Impersonation URL copied to clipboard!\n\n📋 URL: ${redirectUrl}\n\n⚠️ IMPORTANT: Open this URL in an INCOGNITO/PRIVATE window to impersonate ${name || email}`);
          }).catch(() => {
            setMessage(`✅ Impersonation URL generated for ${name || email}:\n\n${redirectUrl}\n\n⚠️ IMPORTANT: Copy this URL and open it in an INCOGNITO/PRIVATE window`);
          });
          
          // Don't auto-open in same browser
          // window.open(redirectUrl, '_blank');
        } else {
          setMessage('Error: No impersonation URL received');
          setTimeout(() => setMessage(''), 5000);
        }
      }
    } catch (error) {
      setMessage(`Error impersonating user: ${error}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshUsers = async () => {
    setIsLoading(true);
    try {
      await refreshUsers();
      setMessage('Users refreshed');
    } catch (error) {
      setMessage(`Error refreshing users: ${error}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSaveRolePermissions = async (role: UserRole, permissions: Permission[]) => {
    setIsSavingPermissions(true);
    try {
      const response = await updateRolePermissions(role, permissions);
      if (response.error) {
        setMessage(`Error: ${response.error}`);
      } else {
        setMessage(`Role permissions updated for ${role} role`);
      }
    } catch (error) {
      setMessage(`Error saving role permissions: ${error}`);
    } finally {
      setIsSavingPermissions(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Shield className="w-8 h-8 text-red-600" />
          Admin Panel - User Role Management
        </h2>
        <p className="text-gray-600">
          Manage user roles and permissions for the financial dashboard.
        </p>
      </div>

      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <p className="text-green-800 whitespace-pre-line flex-1 font-mono text-sm break-all">{message}</p>
          </div>
        </div>
      )}

      {/* Access Control Check - only show after permissions load */}
      {!isLoadingPermissions && !canManageUsers && (
        <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">You don't have permission to manage users.</p>
          </div>
        </div>
      )}

      {/* Current User Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Current User
        </h3>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Email:</strong> {user?.emailAddresses[0]?.emailAddress}
          </p>
          <p className="text-sm text-blue-800">
            <strong>Role:</strong> {isAdmin ? 'Admin' : 'User'}
          </p>
          <p className="text-sm text-blue-800">
            <strong>User ID:</strong> {user?.id}
          </p>
          <p className="text-sm text-blue-800">
            <strong>Can Manage Users:</strong> {canManageUsers ? 'Yes' : 'No'}
          </p>
        </div>
      </div>

      {/* Add New User */}
      {canManageUsers && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-600" />
            Add New User
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@example.com"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
              </label>
              <input
                type="text"
                value={newUser.first_name || ''}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role *
              </label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <option value="Lender">Lender</option>
                <option value="Seller">Seller</option>
                <option value="Buyer">Buyer</option>
                <option value="Viewer">Viewer</option>
                {isSuperAdmin && <option value="Admin">Admin</option>}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddUser}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                Add User
              </button>
            </div>
          </div>
          
          {/* Deal Access Selection */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Deal Access (Optional)
              </label>
              <button
                type="button"
                onClick={handleAddDealAccess}
                disabled={isLoading || isLoadingDeals}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50"
              >
                <Plus className="w-3 h-3" />
                Add Deal
              </button>
            </div>
            
            {isLoadingDeals ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="ml-2 text-sm text-gray-600">Loading deals...</span>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDeals.map((dealAccess, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <select
                        value={dealAccess.dealId}
                        onChange={(e) => handleUpdateDealAccess(index, 'dealId', e.target.value)}
                        disabled={isLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Select a deal...</option>
                        {availableDeals.map((deal) => (
                          <option key={deal.deal_id} value={deal.deal_id}>
                            {deal.business_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-32">
                      <select
                        value={dealAccess.accessType}
                        onChange={(e) => handleUpdateDealAccess(index, 'accessType', e.target.value)}
                        disabled={isLoading}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="read">Read Only</option>
                        <option value="write">Read & Write</option>
                        <option value="admin">Full Access</option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDealAccess(index)}
                      disabled={isLoading}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {selectedDeals.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No deal access selected. User will have access based on their role only.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Management Table */}
      {canManageUsers && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              User Management
            </h3>
            <button
              onClick={handleRefreshUsers}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          
          {isLoading && allUsers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Current Role</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                    {isSuperAdmin && <th className="text-left py-3 px-4 font-semibold text-gray-700">Super Admin</th>}
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Change Role</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((user) => (
                    <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-700">{user.email}</td>
                      <td className="py-3 px-4 text-gray-700">
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : user.first_name || 'N/A'
                        }
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.role === 'Admin' ? 'bg-red-100 text-red-800' :
                          user.role === 'Buyer' ? 'bg-purple-100 text-purple-800' :
                          user.role === 'Lender' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'Seller' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleToggleSuperAdmin(user.user_id, user.metadata?.is_super_admin || false)}
                            disabled={isLoading}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                              user.metadata?.is_super_admin
                                ? 'bg-purple-100 text-purple-800 border border-purple-300 hover:bg-purple-200'
                                : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                            }`}
                            title={user.metadata?.is_super_admin ? 'Click to revoke Super Admin' : 'Click to grant Super Admin'}
                          >
                            {user.metadata?.is_super_admin ? '⭐ Super Admin' : 'Make Super Admin'}
                          </button>
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <select
                          value={user.role}
                          onChange={(e) => handleUpdateUserRole(user.user_id, e.target.value as UserRole)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={isLoading}
                        >
                          <option value="Lender">Lender</option>
                          <option value="Seller">Seller</option>
                          <option value="Buyer">Buyer</option>
                          <option value="Viewer">Viewer</option>
                          {isSuperAdmin && <option value="Admin">Admin</option>}
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleImpersonateUser(user.user_id, user.email)}
                            disabled={isLoading || user.user_id === user?.id}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            title={user.user_id === user?.id ? "Cannot impersonate yourself" : "Impersonate this user (opens in new tab)"}
                          >
                            Impersonate
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => handleToggleUserStatus(user.user_id, user.is_active)}
                            disabled={isLoading}
                            className={`text-sm font-medium disabled:opacity-50 ${
                              user.is_active 
                                ? 'text-orange-600 hover:text-orange-800' 
                                : 'text-green-600 hover:text-green-800'
                            }`}
                          >
                            {user.is_active ? 'Deactivate' : 'Reactivate'}
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => handlePermanentlyDeleteUser(user.user_id)}
                            disabled={isLoading}
                            className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                            title="Permanently delete user and all their deals"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {allUsers.length === 0 && !isLoading && (
                <div className="text-center py-8 text-gray-500">
                  No users found. Add a user to get started.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Role Permission Customization */}
      {canManageUsers && (
        <div className="mt-8">
          <RolePermissionManager 
            onSave={handleSaveRolePermissions}
            isLoading={isSavingPermissions}
          />
        </div>
      )}

      {/* Role Descriptions Summary */}
      <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Descriptions Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="p-4 bg-red-50 rounded-lg">
            <h4 className="font-semibold text-red-900 mb-2">Admin</h4>
            <ul className="text-sm text-red-800 space-y-1">
              <li>• Full access to all pages</li>
              <li>• Can manage user roles</li>
              <li>• Access to admin panel</li>
              <li>• Export data capabilities</li>
              <li>• Manage all deals</li>
            </ul>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-2">Buyer</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Create and manage deals</li>
              <li>• Access to financial data</li>
              <li>• Manage deal documents</li>
              <li>• Grant deal access to users</li>
              <li>• Export data capabilities</li>
            </ul>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Lender</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Access to financial analysis</li>
              <li>• Lending recommendations</li>
              <li>• Risk assessment tools</li>
              <li>• Cash flow analysis</li>
              <li>• Export data capabilities</li>
            </ul>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Seller</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• Access to financial analysis</li>
              <li>• Business valuation insights</li>
              <li>• Market position analysis</li>
              <li>• Performance metrics</li>
            </ul>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Viewer</h4>
            <ul className="text-sm text-gray-800 space-y-1">
              <li>• Read-only access to financial data</li>
              <li>• Basic analytics viewing</li>
              <li>• Limited feature access</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Use the Role Permission Customization section above to modify the specific permissions for each role. 
            The descriptions here show the default permissions, but they can be customized by administrators.
          </p>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-amber-900 mb-2">Database Integration</h3>
        <div className="text-sm text-amber-800 space-y-2">
          <p><strong>Role-Based Access Control (RBAC):</strong></p>
          <p>1. User roles are now stored in the Supabase database</p>
          <p>2. Role changes are persisted and audited</p>
          <p>3. Permissions are enforced at the database level with Row Level Security (RLS)</p>
          <p>4. All user management operations are logged in the audit trail</p>
          <p><strong>Features:</strong></p>
          <p>• Real-time role updates with database persistence</p>
          <p>• Comprehensive audit logging for all admin actions</p>
          <p>• Secure permission checking at the database level</p>
          <p>• User session tracking and management</p>
        </div>
      </div>
    </div>
  );
}
